/* eslint-disable react/jsx-props-no-spreading */
import { Box } from '@mui/material';
import UPInput from 'components/input';
import PasswordsMatchText from 'components/password-strength/PasswordsMatchText';
import PasswordStrength from 'components/password-strength/PasswordStrength';
import { useCallback, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { logoutAction, passwordTokenValidityAction } from 'store/actions/auth-actions';
import { setPasswordAsync } from 'store/actions/profile-actions';
import { authStateSelector, setPasswordTokenValiditySelector } from 'store/selectors/auth-selectors';
import { setPasswordErrorSelector, setPasswordSuccessSelector } from 'store/selectors/profile-selectors';
import {
  GENERAL__SAVE_BUTTON,
  LOGIN__FORGOT_PASSWORD,
  LOGIN__PAGE_QUOTE,
  LOGIN__PASSWORD_LABEL,
  LOGIN__PASSWORD_MIN_LENGTH_ERROR,
  SET_PASSWORD__CONFIRM_PASSWORD_LABEL,
  SET_PASSWORD__LINK_EXPIRED,
  SET_PASSWORD__TITLE,
  SET_PASSWORD__UNAUTHORISED_REQUEST,
} from 'translations/constants';
import ISetPassword from 'types/profile/ISetPassword';
import { getInputErrorText, validatePassword } from 'utils/helpers';
import AuthFooter from './AuthFooter';
import AuthHeader from './AuthHeader';
import {
  AuthContainer,
  AuthLeftContainer,
  AuthRightContainer,
  AuthSignIn,
  AuthSignInContainer,
  AuthSignInExpired,
  AuthTitle,
  AuthUPButton,
  ErrorText,
} from './styles';

const PasswordChangeInitialState: ISetPassword = {
  password: '',
  confirmedPassword: '',
};

const SetPassword = (): JSX.Element => {
  const { uniqueKey } = useParams();
  const navigate = useNavigate();
  const setPasswordSuccess = useAppSelector(setPasswordSuccessSelector);
  const setPasswordError = useAppSelector(setPasswordErrorSelector);
  const isAuthenticated = useAppSelector(authStateSelector);
  const isLinkTokenValid = useAppSelector(setPasswordTokenValiditySelector);
  const {
    handleSubmit,
    control,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: { errors, isDirty },
  } = useForm<ISetPassword>({ defaultValues: PasswordChangeInitialState });
  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const passwordWatch = watch('password');
  const confirmedPasswordWatch = watch('confirmedPassword');

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(logoutAction(false));
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    dispatch(passwordTokenValidityAction(uniqueKey));
  }, [uniqueKey, dispatch]);

  const onSave: SubmitHandler<ISetPassword> = useCallback(() => {
    const values = getValues();
    if (isDirty) {
      dispatch(setPasswordAsync({ userUniqueKey: uniqueKey, password: values.confirmedPassword }));
    }
  }, [dispatch, getValues, isDirty, uniqueKey]);

  useEffect(() => {
    if (setPasswordSuccess) {
      navigate(`/auth/login`);
    }
  }, [navigate, setPasswordSuccess]);

  const goToForgotPassword = useCallback(() => {
    navigate('/forgotpassword');
  }, [navigate]);

  return (
    <Box>
      <AuthHeader />
      <AuthContainer sx={{ display: 'flex' }}>
        <AuthLeftContainer>
          <AuthTitle>{t(LOGIN__PAGE_QUOTE)}</AuthTitle>
        </AuthLeftContainer>
        <AuthRightContainer>
          {!isLinkTokenValid && (
            <AuthSignInContainer>
              <AuthSignInExpired>{t(SET_PASSWORD__LINK_EXPIRED)}</AuthSignInExpired>
              <AuthUPButton
                text={t(LOGIN__FORGOT_PASSWORD)}
                onClick={() => goToForgotPassword()}
                sx={{ marginTop: '16px' }}
              />
            </AuthSignInContainer>
          )}
          {isLinkTokenValid && (
            <AuthSignInContainer>
              <AuthSignIn>{t(SET_PASSWORD__TITLE)}</AuthSignIn>
              <Box width="100%" display="flex" flexDirection="column" gap="8px">
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: true,
                    minLength: { value: 8, message: t(LOGIN__PASSWORD_MIN_LENGTH_ERROR) },
                    maxLength: 100,
                  }}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      label={t(LOGIN__PASSWORD_LABEL)}
                      placeholder={t(LOGIN__PASSWORD_LABEL)}
                      error={!!errors.password}
                      required
                      helperText={getInputErrorText(errors, 'password')}
                      onChange={field.onChange}
                      type="password"
                    />
                  )}
                />
                <Box width="100%" height={30}>
                  <PasswordStrength password={passwordWatch} />
                </Box>
              </Box>
              <Box width="100%" display="flex" flexDirection="column" gap="8px">
                <Controller
                  name="confirmedPassword"
                  control={control}
                  rules={{
                    required: true,
                    minLength: { value: 8, message: t(LOGIN__PASSWORD_MIN_LENGTH_ERROR) },
                    maxLength: 100,
                  }}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      label={t(SET_PASSWORD__CONFIRM_PASSWORD_LABEL)}
                      placeholder={t(SET_PASSWORD__CONFIRM_PASSWORD_LABEL)}
                      error={!!errors.confirmedPassword}
                      required
                      helperText={getInputErrorText(errors, 'confirmedPassword')}
                      onChange={field.onChange}
                      type="password"
                    />
                  )}
                />
                <Box width="100%" height={20}>
                  {confirmedPasswordWatch.length > 0 && (
                    <PasswordsMatchText passwordsMatch={passwordWatch === confirmedPasswordWatch} />
                  )}
                </Box>
              </Box>
              {setPasswordError && <ErrorText>{t(SET_PASSWORD__UNAUTHORISED_REQUEST)}</ErrorText>}
              <AuthUPButton
                text={t(GENERAL__SAVE_BUTTON)}
                onClick={() => handleSubmit(onSave)()}
                disabled={!validatePassword(passwordWatch) || passwordWatch !== confirmedPasswordWatch}
              />
            </AuthSignInContainer>
          )}
        </AuthRightContainer>
      </AuthContainer>
      <AuthFooter />
    </Box>
  );
};

export default SetPassword;
